import { useEffect, useState, useRef, useCallback } from "react";
import Pod from "components/Pod/Pod";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import "./EditCourse.scss";
import ClickBoundary from "./components/ClickBoundary/ClickBoundary";
import EditableModule from "./components/EditableModule/EditableModule";
import { EditModeProvider, useEditMode } from "./components/EditModeContext/EditModeContext";
import PublishStatus from "./components/PublishStatus/PublishStatus";
import ChangeList from "./components/ChangeList/ChangeList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { useLocation } from "react-router-dom";
import { DataFetcher } from "components/DataFetcher";
import { getFetchOptions } from "utils/fetch";
import { useModal } from "components/Modal/context/ModalContext";
import { ModalButtons } from "components/Modal/ModalButtons";
import EditButtonGroup from "./components/EditButton/EditButtonGroup";

const EditCourseInner = () => {
	const { activeField, setActiveField, cancelAll } = useEditMode();
	const location = useLocation();
	const [fetchData, setFetchData] = useState({});
	const [data, setData] = useState(null);
	const [nameDraft, setNameDraft] = useState("");
	const [descDraft, setDescDraft] = useState("");
	const [imageUrlDraft, setImageUrlDraft] = useState("");
	const [imageFileDraft, setImageFileDraft] = useState(null);
	const [changes, setChanges] = useState(null);
	const courseId = useRef("");
	const courseDraft = useRef({});
	const { openModal } = useModal();

	// hidden file input for image uploads
	const fileInputRef = useRef(null);

	const setFetchRequestData = useCallback((url, options = null) => {
		setFetchData({ url: url, options: options });
	}, []);

	// ---- Added helpers for per-field undo (no other changes) ----
	const fieldGetters = {
		Name: () => nameDraft,
		Description: () => descDraft,
		ImageUrl: () => imageUrlDraft,
	};

	const fieldSetters = {
		Name: setNameDraft,
		Description: setDescDraft,
		ImageUrl: setImageUrlDraft,
	};

	const getPublished = (field) => (data?.[field] ?? "");

	const isEdited = (field) => fieldGetters[field]() !== getPublished(field);

	const commitDraft = (overrides) => {
		const body = {
			Id: courseId.current,
			Name: overrides?.Name ?? nameDraft,
			Description: overrides?.Description ?? descDraft,
			ImageUrl: overrides?.ImageUrl ?? imageUrlDraft,
			ImageFile: overrides && Object.prototype.hasOwnProperty.call(overrides, "ImageFile")
				? overrides.ImageFile
				: imageFileDraft,
			Modules: data.Modules, // no draft data yet, but placeholder for when we allow edits to modules & submodules
			PublishedCourse: data
		};
		const options = getFetchOptions({ method: "PUT", body });
		setFetchRequestData("/api/CourseDraft", options);
	};

	const handleUndoField = (field) => {
		const publishedValue = getPublished(field);
		fieldSetters[field](publishedValue);
		if (field === "ImageUrl") {
			setImageFileDraft(null);
		}
		commitDraft({
			[field]: publishedValue,
			...(field === "ImageUrl" ? { ImageFile: null } : {})
		});
		cancelAll();
	};

	//This might very well become a hook in future 
	useEffect(() => {
		if (!data) {
			return;
		}

		if (data.Id) {
			let parsedDraft = {};
			if (data.DraftData) {
				try {
					parsedDraft = JSON.parse(data.DraftData) || {};
				}
				catch {
					console.warn("Invalid DraftData JSON:", data.DraftData);
					parsedDraft = {};
				}
			}
			courseDraft.current = parsedDraft;

			const draftName = parsedDraft.Name ?? data.Name ?? "";
			const draftDesc = parsedDraft.Description ?? data.Description ?? "";
			const draftImage = parsedDraft.ImageUrl || data.ImageUrl || "";
			setNameDraft(draftName);
			setDescDraft(draftDesc);
			setImageUrlDraft(draftImage);

			const detectedChanges = [];

			if (
				draftName !== data.Name ||
				draftDesc !== data.Description ||
				draftImage !== data.ImageUrl
			) {
				detectedChanges.push({
					level: "Course",
					action: "Edited",
					name: `${draftName || data.Name}`
				});

				if (draftName !== data.Name) {
					detectedChanges.push({ label: "Course title updated", field: "Name" });
				}
				if (draftDesc !== data.Description) {
					detectedChanges.push({ label: "Course description updated", field: "Description" });
				}
				if (draftImage !== data.ImageUrl) {
					detectedChanges.push({ label: "New course image uploaded", field: "ImageUrl" });
				}
			}
			setChanges(detectedChanges);

		}
		else {
			setFetchRequestData(`/api/v3/Course/${courseId.current}`);
		}
	}, [data, setFetchRequestData]);

	useEffect(() => {
		if (location.hash) {
			courseId.current = parseInt(location.hash.slice(1).split("-")[1], 10);
			setFetchRequestData(`/api/v3/Course/${courseId.current}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.hash]);

	const handleData = useCallback((fetchedData) => {
		if (fetchedData) {
			setData(fetchedData);
		}
	}, []);

	const handleFieldCommit = () => {
		const options = getFetchOptions({
			method: "PUT",
			body: {
				Id: courseId.current,
				Name: nameDraft,
				Description: descDraft,
				ImageUrl: imageUrlDraft,
				ImageFile: imageFileDraft,
				Modules: data.Modules, // no draft data yet, but placeholder for when we allow edits to modules & submodules
				PublishedCourse: data
			}
		});
		setFetchRequestData("/api/CourseDraft", options);
		// Close the edit mode of the field
		cancelAll();
	};

	const handleConfirmDiscard = (yes) => {
		if (yes) {
			const options = getFetchOptions({
				method: "DELETE"
			});
			setFetchRequestData(`/api/CourseDraft/${courseId.current}`, options);

			setNameDraft(data?.Name ?? "");
			setDescDraft(data?.Description ?? "");
			setImageUrlDraft(data?.ImageUrl ?? "");
			setImageFileDraft(null);

			courseDraft.current = {};
			setChanges([]);

			setData((prev) => (prev ? { ...prev, DraftData: "" } : prev));

			setFetchRequestData(`/api/v3/Course/${courseId.current}`);
		}
	};


	const handleConfirmPublish = (yes) => {
		if (yes) {
			const options = getFetchOptions({
				method: "PUT",
				body: {
					Id: courseId.current,
					Name: nameDraft,
					Description: descDraft,
					ImageUrl: imageUrlDraft,
					DraftData: data.DraftData
				}
			});
			setFetchRequestData(`/api/v3/Course`, options);
		}
	};

	const handleOpenDiscardModal = () => {
		openModal(
			null,
			"Are you sure you want to discard changes to this course?",
			ModalButtons.YesNo,
			handleConfirmDiscard
		);
	};

	const handleOpenPublishModal = () => {
		openModal(
			null,
			"Are you sure you want to publish these changes? This will permanently overwrite the currently published course.",
			ModalButtons.YesNo,
			handleConfirmPublish
		);
	};

	const handleNameKeyDown = (e) => {
		if (e.key === "Escape") {
			cancelAll();
		}
		else if (e.key === "Enter") {
			e.currentTarget.blur();
		}
	};

	const handleDescKeyDown = (e) => {
		if (e.key === "Escape") {
			cancelAll();
		}
		else if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			e.currentTarget.blur();
		}
	};


	const openImagePicker = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
			fileInputRef.current.click();
		}
	};

	const handleImageFileChange = async (e) => {
		const file = (e.target.files && e.target.files[0]) || null;
		if (!file) {
			return;
		}

		const reasons = [];

		// 1) Size check (< 300 KB)
		if (file.size >= 300 * 1024) {
			reasons.push("File must be smaller than 300 KB.");
		}

		// 2) Validate dimensions/aspect using an object URL
		const objectUrl = URL.createObjectURL(file);
		const img = new Image();

		img.onload = () => {
			try {
				const w = img.naturalWidth || img.width;
				const h = img.naturalHeight || img.height;

				// Max width 1200px
				if (w > 1200) {
					reasons.push("Image width must be 1200px or less.");
				}

				// Aspect ratio: height between 60% and 100% of width (0.6 to 1.0)
				const ratio = h / w;
				if (ratio < 0.6 || ratio > 1.0) {
					reasons.push("Image height must be between 60% and 100% of its width (5:3 up to square).");
				}
			} finally {
				URL.revokeObjectURL(objectUrl);
			}

			// If any validation failed, show modal and exit
			if (reasons.length > 0) {
				if (fileInputRef.current) fileInputRef.current.value = "";
				openModal(
					null,
					"We could not use that image:\n- " + reasons.join("\n- "),
					ModalButtons.Ok
				);
				return;
			}

			// 3) All validations passed -> proceed
			const reader = new FileReader();
			reader.onload = () => {
				const fileData = {
					FileName: file.name,
					MimeType: file.type,
					Base64: reader.result
				};
				setImageFileDraft(fileData);
				const options = getFetchOptions({
					method: "PUT",
					body: {
						Id: courseId.current,
						Name: nameDraft,
						Description: descDraft,
						ImageUrl: imageUrlDraft,
						ImageFile: fileData, // direct, guaranteed non-null
						Modules: data.Modules,
						PublishedCourse: data
					}
				});
				setFetchRequestData("/api/CourseDraft", options);
				cancelAll();
			};
			reader.onerror = () => {
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}
				console.error("Image upload/convert error");
				openModal(null, "We could not read that image file.", ModalButtons.YesNo);
			};
			reader.readAsDataURL(file);
		};

		img.onerror = () => {
			URL.revokeObjectURL(objectUrl);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
			openModal(null, "We could not load that image to validate it.", ModalButtons.YesNo);
		};

		img.src = objectUrl;
	};

	return (
		<>
			<DataFetcher fetchData={fetchData} setData={handleData} />
			{data !== null && (
				<div className="edit-course" onMouseDown={() => {setTimeout(cancelAll, 0);}}>
					<div className="grid-two-col gutters">
						<Pod title="Edit Course">
							<div className="course-container">
								<div className="course-container-details">
									<div className="course-container-details-text">
										{/* Course Name */}
										<ClickBoundary>
											{activeField?.scope === "course" && activeField.field === "name" ? (
												<InputText
													autoFocus
													value={nameDraft}
													onChange={(e) => setNameDraft(e.target.value)}
													onKeyDown={handleNameKeyDown}
													onBlur={() =>
														handleFieldCommit()
													}
													className="title-textbox"
												/>
											) : (
												<div className="course-title">
														<h1>{nameDraft}</h1>
														<EditButtonGroup
															onEdit={() => setActiveField({ scope: "course", field: "name" })}
															showUndo={isEdited("Name")}
															onUndo={() => handleUndoField("Name")}
														/>
												</div>
											)}
										</ClickBoundary>

										{/* Course Description */}
										<ClickBoundary>
											{activeField?.scope === "course" && activeField.field === "desc" ? (
												<InputTextarea
													autoFocus
													value={descDraft}
													onChange={(e) => setDescDraft(e.target.value)}
													onKeyDown={handleDescKeyDown}
													onBlur={() =>
														handleFieldCommit()
													}
													rows={4}
													autoResize
													className="description-textbox"
												/>
											) : (
												<div className="course-container-details-text-description">
														<p>{descDraft}</p>
														<EditButtonGroup
															mode="floating"
															onEdit={() => setActiveField({ scope: "course", field: "desc" })}
															showUndo={isEdited("Description")}
															onUndo={() => handleUndoField("Description")}
														/>
												</div>
											)}
										</ClickBoundary>
									</div>

									<div className="course-container-details-image">
										<img src={imageUrlDraft} alt={nameDraft} className="course-image" />
										<div className="course-container-details-image-edit">
											{/* Hidden input for picking an image */}
											<input
												ref={fileInputRef}
												type="file"
												accept="image/*"
												style={{ display: "none" }}
												onChange={handleImageFileChange}
											/>
											<EditButtonGroup
												static
												editText="Edit Image"
												onEdit={openImagePicker}
												showUndo={isEdited("ImageUrl")}
												onUndo={() => handleUndoField("ImageUrl")}
											/>
										</div>
									</div>
								</div>

								<h2>Modules</h2>
								{Array.isArray(data.Modules) &&
									data.Modules
										.slice()
										.sort((a, b) => (a.Position ?? 0) - (b.Position ?? 0))
										.map((m) => <EditableModule key={m.Id} module={m} />)}

								<div className="course-container-addnew">
									<div className="course-container-addnew-button">
										<p>
											Add New Module <FontAwesomeIcon icon={faPlusCircle} style={{ color: "#FFFFFF" }} />
										</p>
									</div>
								</div>
							</div>
						</Pod>

						<Pod title="Changes" mobileOrder={1} sticky>
							<PublishStatus
								status={data.CourseStatus}
								courseId={data.Id}
								handleOpenDiscardModal={handleOpenDiscardModal}
								handleOpenPublishModal={handleOpenPublishModal}
							/>
							{changes === null ? (
								<p>Loading changes...</p>
							) : (
									<ChangeList
										status={data.CourseStatus}
										changes={changes}
										onUndoField={handleUndoField}
										handleOpenDiscardModal={handleOpenDiscardModal}
									/>
							)}
						</Pod>
					</div>
				</div>
			)}
		</>
	);
};

const EditCourse = () => (
	<EditModeProvider>
		<EditCourseInner />
	</EditModeProvider>
);
export default EditCourse;
